import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Banknote, Wallet, Receipt, Landmark, Trash2 } from "lucide-react";

const eur = (v) => `€ ${(v || 0).toFixed(2)}`;
const today = () => new Date().toLocaleDateString("en-CA");
const shift = (d, n) => { const x = new Date(`${d}T12:00`); x.setDate(x.getDate() + n); return x.toLocaleDateString("en-CA"); };
const hm = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

function Kpi({ icon: Icon, label, value, testId, tone = "" }) {
  return (
    <Card className={`p-4 border border-stone-200 rounded-xl shadow-none ${tone}`}>
      <div className="text-[11px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Icon size={13}/>{label}</div>
      <div className="text-2xl font-semibold mt-1" data-testid={testId}>{value}</div>
    </Card>
  );
}

function AccountPicker() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["bar-config"], queryFn: async () => (await api.get("/bar/config")).data });
  const save = useMutation({
    mutationFn: async (account_id) => (await api.put("/bar/config", { account_id })).data,
    onSuccess: (d) => { qc.setQueryData(["bar-config"], d); toast.success("Cassa del bar aggiornata"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  if (!data) return null;
  return (
    <Card className="p-4 border border-stone-200 rounded-xl shadow-none flex flex-col sm:flex-row sm:items-center gap-3" data-testid="bar-account-config">
      <div className="flex items-center gap-2 text-sm text-stone-700 flex-1"><Landmark size={16} className="text-[#1C352D]"/>
        Gli incassi in contanti del bar vengono versati automaticamente nella cassa:</div>
      <Select value={data.account_id || ""} onValueChange={(v) => save.mutate(v)}>
        <SelectTrigger className="sm:w-64" data-testid="bar-account-select"><SelectValue placeholder="Scegli la cassa"/></SelectTrigger>
        <SelectContent>{data.accounts.map((a) => <SelectItem key={a.id} value={a.id} data-testid={`bar-account-${a.id}`}>{a.name}</SelectItem>)}</SelectContent>
      </Select>
    </Card>
  );
}

function Table({ title, rows, cols, testId, empty }) {
  return (
    <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden" data-testid={testId}>
      <div className="px-4 py-3 border-b border-stone-100 text-[11px] uppercase tracking-widest text-stone-500">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] text-stone-500 text-left">{cols.map(([k, l, r]) => <th key={k} className={`px-4 py-2 font-medium ${r ? "text-right" : ""}`}>{l}</th>)}</tr></thead>
          <tbody className="divide-y divide-stone-100">
            {rows.map((row, i) => <tr key={i}>{cols.map(([k, , r, f]) => <td key={k} className={`px-4 py-2 ${r ? "text-right tabular-nums whitespace-nowrap" : ""}`}>{f ? f(row) : row[k]}</td>)}</tr>)}
          </tbody>
        </table>
        {!rows.length && <div className="px-4 py-6 text-center text-sm text-stone-400">{empty}</div>}
      </div>
    </Card>
  );
}

const MONEY_COLS = [["contanti", "Contanti", true, (r) => eur(r.contanti)], ["wallet", "Wallet", true, (r) => eur(r.wallet)], ["total", "Totale", true, (r) => <b>{eur(r.total)}</b>]];

export default function BarReport() {
  const qc = useQueryClient();
  const [range, setRange] = useState("day");
  const [date, setDate] = useState(today());
  const from = range === "day" ? date : shift(date, range === "week" ? -6 : -29);
  const { data: r } = useQuery({ queryKey: ["bar-report", from, date], queryFn: async () => (await api.get("/bar/report", { params: { date_from: from, date_to: date } })).data });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/bar/cash-sales/${id}`)).data,
    onSuccess: () => { toast.success("Incasso annullato e tolto dalla cassa"); qc.invalidateQueries({ queryKey: ["bar-report"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1200px] mx-auto space-y-5" data-testid="bar-report-page">
      <PageHeader title="Incassi bar" subtitle="Controllo giornaliero delle vendite dei cassieri: contanti versati in cassa e addebiti sul wallet degli studenti."/>
      <AccountPicker/>
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={range} onValueChange={setRange}>
          <TabsList>
            <TabsTrigger value="day" data-testid="bar-range-day">Giorno</TabsTrigger>
            <TabsTrigger value="week" data-testid="bar-range-week">Ultimi 7 giorni</TabsTrigger>
            <TabsTrigger value="month" data-testid="bar-range-month">Ultimi 30 giorni</TabsTrigger>
          </TabsList>
        </Tabs>
        <Input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value || today())} className="w-44" data-testid="bar-report-date"/>
        {range !== "day" && <span className="text-xs text-stone-500">dal {new Date(`${from}T12:00`).toLocaleDateString("it-IT")}</span>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Banknote} label="Contanti in cassa" value={eur(r?.contanti)} testId="bar-kpi-cash" tone="bg-amber-50/60"/>
        <Kpi icon={Wallet} label="Addebiti wallet" value={eur(r?.wallet)} testId="bar-kpi-wallet"/>
        <Kpi icon={Receipt} label="Totale vendite" value={eur(r?.total)} testId="bar-kpi-total"/>
        <Kpi icon={Receipt} label="N. vendite" value={r?.count ?? 0} testId="bar-kpi-count"/>
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <Table title="Riepilogo per prodotto" testId="bar-by-product" rows={r?.by_product || []} empty="Nessuna vendita nel periodo."
          cols={[["name", "Prodotto"], ["quantity", "Pezzi", true], ...MONEY_COLS]}/>
        <Table title="Riepilogo per cassiere" testId="bar-by-cashier" rows={r?.by_cashier || []} empty="Nessuna vendita nel periodo."
          cols={[["name", "Cassiere"], ["count", "Vendite", true], ...MONEY_COLS]}/>
      </div>
      <Table title="Dettaglio vendite" testId="bar-sales-list" rows={r?.sales || []} empty="Nessuna vendita nel periodo." cols={[
        ["created_at", "Quando", false, (s) => <span className="text-xs text-stone-500 whitespace-nowrap">{hm(s.created_at)}</span>],
        ["buyer", "Acquirente", false, (s) => <>{s.buyer} <span className="text-[11px] text-stone-400">{s.buyer_type === "teacher" ? "docente" : s.buyer_type === "cliente" ? "cliente" : "studente"}</span></>],
        ["label", "Prodotto"], ["cashier", "Cassiere"],
        ["method", "Pagamento", false, (s) => <span className={`text-[11px] px-1.5 py-0.5 rounded ${s.method === "contanti" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`}>{s.method === "contanti" ? "contanti" : "wallet"}</span>],
        ["amount", "Importo", true, (s) => eur(s.amount)],
        ["x", "", true, (s) => s.method === "contanti" && <button onClick={() => window.confirm("Annullare questo incasso? Verrà tolto anche dalla cassa.") && del.mutate(s.id)} className="text-stone-400 hover:text-red-600" title="Annulla incasso" data-testid={`bar-sale-delete-${s.id}`}><Trash2 size={13}/></button>],
      ]}/>
    </div>
  );
}
